import React, { useState, useEffect, useRef } from 'react';
import TrainingFilters from './training_overview_page/TrainingFilters';
import TrainingSort from './training_overview_page/TrainingSort';
import TrainingList from './training_overview_page/TrainingList';
import useFilteredResults from './training_overview_page/useFilteredResults';
import Pagination from './training_overview_page/Pagination';
import {
  getActiveFiltersFromCookie,
  getFilterFromCookie,
  deleteCookie,
} from './training_overview_page/cookieHelpers';

const TrainingOverviewPage = ({ obj, results }) => {
  const [audienceFilter, setAudienceFilter] = useState('');
  const [educationFilter, setEducationFilter] = useState('');
  const [learningFormatFilter, setLearningFormatFilter] = useState('');
  const [professionalGroupFilter, setProfessionalGroupFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [resultSort, setResultSort] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const hasInteractedWithPagination = useRef(false);
  const topRef = useRef(null);
  const itemsPerPage = 10;

  const filters = {
    audienceFilter,
    educationFilter,
    learningFormatFilter,
    professionalGroupFilter,
    providerFilter,
    startDateFilter,
    endDateFilter,
    searchQuery,
    resultSort,
  };

  const filteredResults = useFilteredResults(results, filters);

  useEffect(() => {
    const providerFilter = getFilterFromCookie('providerFilter');
    const allActiveFilters = getActiveFiltersFromCookie('allActiveFilters');

    if (providerFilter) {
      setProviderFilter(providerFilter);
      deleteCookie('providerFilter');
      deleteCookie('allActiveFilters');
      return;
    }

    if (allActiveFilters) {
      const filterActions = {
        audienceFilter: setAudienceFilter,
        educationFilter: setEducationFilter,
        learningFormatFilter: setLearningFormatFilter,
        professionalGroupFilter: setProfessionalGroupFilter,
        providerFilter: setProviderFilter,
        resultSort: setResultSort,
      };

      allActiveFilters.forEach((filter) => {
        const [key, value] = Object.entries(filter)[0];
        filterActions[key]?.(value);
      });

      deleteCookie('allActiveFilters');
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    audienceFilter,
    educationFilter,
    learningFormatFilter,
    resultSort,
    professionalGroupFilter,
    providerFilter,
    startDateFilter,
    endDateFilter,
    searchQuery,
  ]);

  const handlePageChange = (newPage) => {
    hasInteractedWithPagination.current = true;
    setCurrentPage(newPage);
  };

  useEffect(() => {
    if (hasInteractedWithPagination.current && topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentPage]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredResults.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredResults.length);

  return (
    <div className="content-area training-overview">
      <div>
        <TrainingFilters
          {...{
            audienceFilter,
            educationFilter,
            learningFormatFilter,
            professionalGroupFilter,
            providerFilter,
            startDateFilter,
            endDateFilter,
            searchQuery,
            setAudienceFilter,
            setEducationFilter,
            setLearningFormatFilter,
            setProfessionalGroupFilter,
            setProviderFilter,
            setStartDateFilter,
            setEndDateFilter,
            setSearchQuery,
            setResultSort,
            obj,
            results,
          }}
        />
      </div>
      <div className="result-head" ref={topRef}>
        <div className="career-number">
          <p>
            {`${
              filteredResults.length === 0
                ? `0 ${I18n.t('training_overview_page.details.courses')}`
                : filteredResults.length === 1
                  ? `1 ${I18n.t('training_overview_page.details.courses')}`
                  : `${startIndex} - ${endIndex} ${I18n.t('training_overview_page.details.courses')} ${I18n.t('training_overview_page.details.of')} ${filteredResults.length}`
            }`}

            {totalPages > 1 && filteredResults.length > 0 && (
              <span>
                {' '}
                {I18n.t('training_overview_page.details.page')} {currentPage}{' '}
                {I18n.t('training_overview_page.details.of')} {totalPages}
              </span>
            )}
          </p>
        </div>
        <div className="sort">
          <TrainingSort
            {...{
              resultSort,
              setResultSort,
              searchQuery,
              obj,
              results,
            }}
          />
        </div>
      </div>
      {filteredResults.length > 0 ? (
        <>
          <TrainingList
            filteredResults={currentItems}
            filters={filters}
            resultSort={resultSort}
          />
          <Pagination
            totalResults={filteredResults.length}
            resultsPerPage={itemsPerPage}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            totalPages={totalPages}
          />
        </>
      ) : (
        <p>{I18n.t('training_overview_page.index.no_courses_available')}</p>
      )}
    </div>
  );
};

export default TrainingOverviewPage;
